import "./LoginPage.scss";
import axios from "../../api/axios";
import jwt_decode from "jwt-decode";
import constants from "../../constants.js";
import useAuth from "../../Hooks/useAuth.js";
import React, { useEffect } from "react";
import Loading from "../Notification-Loading/Loading/Loading";
import { useCallback } from "react";
import { useRef } from "react";
// import useCommonFunctionHooks from "../../Hooks/useCommonFunctionHooks.js";

const roleRouteMap = {
  [constants.Roles.super_admin]: {
    route: "/company",
    isAdmin: true,
  },
  [constants.Roles.parent]: {
    route: "/company",
    isAdmin: false,
  },
  [constants.Roles.learner]: {
    route: "/company",
    isAdmin: false,
  },
  [constants.Roles.admin]: {
    route: "/company",
    isAdmin: true,
  },
  [constants.Roles.trainer]: {
    route: "/company",
    isAdmin: true,
  },
  [constants.Roles.tutor]: {
    route: "/company",
    isAdmin: true,
  },
  [constants.Roles.evaluator]: {
    route: "/company",
    isAdmin: true,
  },
  [constants.Roles.hr]: {
    route: "/company",
    isAdmin: false,
  },
  [constants.Roles.creator]: {
    route: "/company",
    isAdmin: true,
  },
};

const LoginPage = () => {
  const {
    setAuth,
    setShowNotify,
    navigate,
    setIsAdmin,
    setIsLoaded,
    isLoaded,
    setUserProfilePic,
    setRegistered,
  } = useAuth();

  const hasInitialized = useRef(false);

  // ---------- NAVIGATION HANDLER ----------
  const navigateByRole = useCallback(
    (role, registered) => {
      const config = roleRouteMap[role];

      if (!config) {
        setShowNotify({
          show: true,
          title: "Warning",
          msg: "You are not authorized to access this platform.",
        });
        navigate("/");
        return;
      }

      setIsAdmin(config.isAdmin);

      if (role === constants.Roles.learner && registered === 0) {
        navigate("/registration", { replace: true });
      } else {
        navigate(config.route, { replace: true });
      }
    },
    [setIsAdmin, navigate, setShowNotify],
  );

  const handleCredentialResponse = async (response) => {
    setIsLoaded(true);

    try {
      const { data } = await axios.post(
        "node/auth/validate",
        {
          jwtcredential: response.credential,
          refreshToken: localStorage.getItem("refreshToken"),
        },
        {
          headers: {
            Authorization: "abdjcdifouvn",
            "Content-type": "application/json",
          },
        },
      );

      const { accessToken, refreshToken, picture } = data;

      if (!accessToken || !refreshToken) {
        throw new Error(data.msg);
      }

      localStorage.setItem("tokenKey", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("picture", picture);

      setAuth({ token: accessToken, refreshToken });
      setUserProfilePic(picture);

      // const decoded = jwt_decode(accessToken);
      // setRegistered(decoded.registered);

      // navigateByRole(decoded.role, decoded.registered);
      navigate("/company")
    } catch (err) {
      setShowNotify({
        show: true,
        title: "Warning",
        msg: "We’re unable to verify your email address. Please make sure you’re using a given email or contact gradious team.",
      });
    } finally {
      setIsLoaded(false);
    }
  };

  const handleElectronGoogleLogin = async () => {
  setIsLoaded(true);

  try {
    const response = await window.electronAPI.googleOAuthLogIn();

    if (!response?.success || !response?.idToken) {
      throw new Error(response?.error || "Google login failed");
    }

    const { data } = await axios.post(
      "node/auth/validate",
      {
        jwtcredential: response.idToken,
        refreshToken: localStorage.getItem("refreshToken"),
        isDesktop: true,
      },
      {
        headers: {
          Authorization: "abdjcdifouvn",
          "Content-type": "application/json",
        },
      },
    );

    const { accessToken, refreshToken, picture } = data;

    if (!accessToken || !refreshToken) {
      throw new Error(data.msg);
    }

    localStorage.setItem("tokenKey", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("picture", picture);

    setAuth({ token: accessToken, refreshToken });
    setUserProfilePic(picture);

    navigate("/company");
  } catch (err) {
    console.error("Electron Google login failed:", err);

    setShowNotify({
      show: true,
      title: "Warning",
      msg: "We’re unable to verify your email address. Please make sure you’re using a given email or contact Gradious team.",
    });
  } finally {
    setIsLoaded(false);
  }
};

  // ---------- GOOGLE LOGIN INIT ----------
  const initGoogleLogin = useCallback(async () => {
  const isElectron = window.electronAPI?.clientType === "electron";

  // Electron → Use system browser OAuth with PKCE
  if (isElectron) {
    return;
  }

  // Web → Use Google Identity Services
  if (!window.google) return;

  window.google.accounts.id.initialize({
    client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    callback: handleCredentialResponse,
  });

  window.google.accounts.id.renderButton(
    document.getElementById("signInBtn"),
    {
      theme: "outline",
      size: "large",
    },
  );

  if (!localStorage.getItem("tokenKey")) {
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        console.log(
          "Not displayed:",
          notification.getNotDisplayedReason(),
        );
      }

      if (notification.isSkippedMoment()) {
        console.log(
          "Skipped:",
          notification.getSkippedReason(),
        );
      }

      if (notification.isDismissedMoment()) {
        console.log(
          "Dismissed:",
          notification.getDismissedReason(),
        );
      }
    });
  }
}, []);

  // ---------- MAIN EFFECT ----------
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    const token = localStorage.getItem("tokenKey");
    // No token → show Google login
    if (!token) {
      initGoogleLogin();
      return;
    }

    // Token exists → decode & navigate
    try {
      // const decoded = jwt_decode(token);
      // const { role, registered } = decoded;

      navigate("/company")
    } catch (error) {
      // Invalid token → reset & re-init login
      localStorage.clear();
      initGoogleLogin();
    }
  }, [initGoogleLogin]);

  return (
    !localStorage.tokenKey && (
      <>
        <div className="loginpg"></div>
        <form className="loginForm">
          <div className="image">
            <img
              alt="Gradious"
              className="logo1"
              style={{ width: "200px" }}
              src="https://gradious.com/wp-content/uploads/2021/09/Final-Logo-2.svg"
            />
          </div>
          <div className="tabs">Sign In</div>
          <div className="signInForm">
            <button id="signInBtn" type="button" onClick={
              window.electronAPI?.clientType === "electron"
                ? handleElectronGoogleLogin
                : undefined
            }> SignIn with google </button>
          </div>
        </form>
        {isLoaded && <Loading />}
      </>
    )
  );
};

export default LoginPage;
